type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

// Module-level client log config — set once at startup via configureClientLogging()
let _clientLogEnabled = true;
let _clientLogSamplingRate = 1.0;

export function configureClientLogging(enabled: boolean, samplingRate: number): void {
  _clientLogEnabled = enabled;
  _clientLogSamplingRate = Math.max(0, Math.min(1, samplingRate));
}

interface LogEntry {
  ts: string;
  level: LogLevel;
  ns: string;
  msg: string;
  ctx: Record<string, unknown>;
}

export class WizardLogger {
  private buffer: LogEntry[] = [];
  private shipQueue: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly ns: string) {}

  debug(msg: string, ctx?: Record<string, unknown>) { this._emit('DEBUG', msg, ctx); }
  info(msg: string, ctx?: Record<string, unknown>)  { this._emit('INFO',  msg, ctx); }
  warn(msg: string, ctx?: Record<string, unknown>)  { this._emit('WARN',  msg, ctx); }
  error(msg: string, ctx?: Record<string, unknown>) { this._emit('ERROR', msg, ctx); }

  private _emit(level: LogLevel, msg: string, ctx?: Record<string, unknown>) {
    const entry: LogEntry = {
      ts: new Date().toISOString(),
      level,
      ns: this.ns,
      msg,
      ctx: ctx ?? {},
    };

    if (import.meta.env.DEV) {
      const fn =
        level === 'ERROR' ? console.error :
        level === 'WARN'  ? console.warn  :
        console.log;
      fn(`[${this.ns}] ${msg}`, ctx ?? '');
    }

    this.buffer.push(entry);
    if (this.buffer.length > 500) this.buffer.shift();

    if (level === 'ERROR') {
      this.shipQueue.push(entry);
      // ERROR flushes immediately (debounce 1s)
      if (this.flushTimer !== null) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.flushTimer = setTimeout(() => { this.flush(); this.flushTimer = null; }, 1000);
    } else if (level === 'WARN') {
      this.shipQueue.push(entry);
      this._scheduleFlush();
    }
  }

  private _scheduleFlush() {
    if (this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => { this.flush(); this.flushTimer = null; }, 5000);
  }

  async flush() {
    if (this.shipQueue.length === 0) return;
    if (!_clientLogEnabled) {
      this.shipQueue.length = 0;
      return;
    }
    if (_clientLogSamplingRate < 1.0 && Math.random() > _clientLogSamplingRate) {
      this.shipQueue.length = 0;
      return;
    }
    const batch = this.shipQueue.splice(0, 100);
    try {
      await fetch('/api/logs/client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: batch }),
        keepalive: true,
      });
    } catch { /* shipping must not break UI */ }
  }

  getBuffer(): readonly LogEntry[] {
    return this.buffer;
  }
}

export function createWizardLogger(ns: string): WizardLogger {
  return new WizardLogger(ns);
}
